import React, { useState, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Fab } from "@material-ui/core";
import SaveIcon from "@mui/icons-material/Save";
import logo from "../../assets/logo.png";
import { CsvBuilder } from "filefy";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { REGISTER_COLUMNS } from "../../utils/states";
import { useDirty } from "../../context/dirty";

export const CustomTable = ({
  file,
  update = null,
  remove = null,
  navigate = null,
  setData,
  values,
  columns,
  kms = null,
  title,
  pageSize = 5,
  actions = null,
  onRevisado = null,
  handleSubmit,
  defaultRow = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newRowData, setNewRowData] = useState({});
  const { isDirty } = useDirty();

  const trimNumber = (n) =>
    n.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.");

  const toIsoDate = (value) => {
    if (!value) return new Date().toISOString().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parts = String(value).replace(/\//g, "-").split("-").map((p) => p.trim());
    if (parts.length !== 3) return new Date().toISOString().slice(0, 10);
    let [day, month, year] = parts;
    if (year.length === 4) {
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return `${day}-${month.padStart(2, "0")}-${year.padStart(2, "0")}`;
  };

  const toEsClDate = (iso) => {
    if (!iso) return new Date().toLocaleDateString("es-CL");
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!isoMatch) return iso;
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-CL");
  };

  const parseNumeric = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    return Number(String(value).replace(/[.\s]/g, "").replace(",", ".")) || 0;
  };

  const normalizeServiceRow = (row) => {
    const out = { ...row };
    if (out.interval !== undefined && out.interval !== "") {
      const n = parseNumeric(out.interval);
      out.intervalNum = n;
      out.interval = n > 0 ? trimNumber(n) : String(n);
    }
    if (out.nextService !== undefined && out.nextService !== "") {
      const n = parseNumeric(out.nextService);
      out.nextServiceNum = n;
      out.nextService = n > 0 ? trimNumber(n) : String(n);
    }
    return out;
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return values;
    return values.filter((row) =>
      columns.some((col) => {
        const value = row[col.field];
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
    );
  }, [values, searchTerm, columns]);

  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const exportToCsv = () => {
    const columnTitles = columns.map((col) => col.title);
    const csvData = values.map((row) => columns.map((col) => row[col.field]));
    new CsvBuilder(`data.csv`)
      .setColumns(columnTitles)
      .addRows(csvData)
      .exportFile();
  };

  const effectiveNextKm = (row) => {
    const intervalNum = Number(row.intervalNum) || 0;
    const nextNum = Number(
      String(row.nextServiceNum || row.nextService || "0").split(".").join("")
    ) || 0;
    return row.reset && nextNum > 0 ? nextNum : intervalNum;
  };

  const getServiceStatus = (service) => {
    const nKms = Number(kms) || 0;
    const intervalNum = Number(service.intervalNum) || 0;
    const nextServiceNum = Number(
      String(service.nextServiceNum || service.nextService || "0").split(".").join("")
    );
    const isInspection = intervalNum === 0 && nextServiceNum === 0;

    if (isInspection) return service.reset ? "AL DIA" : "ATRASADA";
    const effective = effectiveNextKm(service);
    if (effective > 0 && nKms < effective) {
      return (effective - nKms) <= 1000 ? "PROXIMO" : "AL DIA";
    }
    if (effective > 0 && nKms >= effective) return "ATRASADA";
    return service.reset ? "AL DIA" : "ATRASADA";
  };

  const exportToPdf = () => {
    const doc = new jsPDF("p", "mm", [216, 279]);
    const fileTitles = REGISTER_COLUMNS.map((v) => v.title);
    const fileData = [[...Object.values(file), file.date]];

    const cleanPdfData = values.map((service) => {
      // Próx. Mant.: nextService guardado si fue reseteado; si está vacío, el intervalo.
      const effective = effectiveNextKm(service);
      const nextDisplay = effective > 0 ? trimNumber(effective) : "-";
      const description = service.reset ? (service.description || "-") : "-";
      const date = service.reset ? (service.date || "-") : "-";
      const worker = service.reset ? (service.worker && service.worker !== "-" ? service.worker : "-") : "-";
      return [
        service.title || "",
        description,
        date,
        worker,
        nextDisplay,
        getServiceStatus(service),
      ];
    });

    if (window.location.pathname === "/dashboard/newfile") {
      doc.setTextColor(0, 102, 204);
      doc.setFontSize(12);
      doc.text("PAUTA DE MANTENCION", 14, 8);
      doc.setFontSize(10);

      doc.setTextColor(155, 153, 153);
      doc.setFontSize(10);
      doc.text(
        "Kurt Moller 985, Linares - 73 2 227032",
        5,
        doc.internal.pageSize.height - 10
      );
      doc.text(
        "La pauta se basa en promedios de mantencion. Revisar el manual de su vehiculo ante cualquier duda",
        5,
        doc.internal.pageSize.height - 5
      );
      const logoEl = document.querySelector("#logo-pdf");
      if (logoEl) {
        doc.addImage(logoEl, "PNG", 190, doc.internal.pageSize.height - 25, 20, 20);
      }
      doc.autoTable({
        head: [fileTitles],
        body: fileData,
      });
    }

    let cols = ["Servicio", "Descripción / Productos", "Fecha", "Mecánico", "Próx. Mant.", "Estado"];

    doc.autoTable({
      head: [cols],
      body: cleanPdfData,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fontSize: 8, fillColor: [102, 187, 154] },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 65 },
        2: { cellWidth: 22 },
        3: { cellWidth: 28 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22 },
      },
      didParseCell: (hookData) => {
        const statusIdx = 5;
        if (hookData.row.raw[statusIdx] === "ATRASADA") {
          hookData.cell.styles.fontStyle = "bold";
          if (hookData.column.index === statusIdx) {
            hookData.cell.styles.textColor = [204, 0, 0];
          }
        } else if (hookData.row.raw[statusIdx] === "PROXIMO") {
          if (hookData.column.index === statusIdx) {
            hookData.cell.styles.textColor = [204, 102, 0];
          }
        } else if (hookData.row.raw[statusIdx] === "AL DIA") {
          if (hookData.column.index === statusIdx) {
            hookData.cell.styles.textColor = [0, 102, 0];
          }
        }
      },
    });

    if (window.location.pathname === "/dashboard/newfile") {
      doc.save(`${file.license}.pdf`);
      return;
    }
    doc.save(`data.pdf`);
  };

  const handleEdit = (row, index) => {
    setEditingRow(index);
    setEditedData({ ...row });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

  const handleSaveEdit = async (oldData, index) => {
    const newData = { ...editedData };

    if (update) {
      await update(newData, newData._id);
    }

    let isFirstEdit = oldData.interval === oldData.nextService;

    if (oldData.nextService !== newData.nextService) {
      if (Number(newData.nextService) > 1000) {
        const nextServiceNumber = newData.nextService;
        newData.nextServiceNum = nextServiceNumber;
        newData.nextService = trimNumber(nextServiceNumber);
        if (isFirstEdit) {
          newData.intervalNum = nextServiceNumber;
          newData.interval = trimNumber(nextServiceNumber);
        }
      } else {
        const nextServiceNumber = Number(newData.nextService) * 1000;
        newData.nextServiceNum = nextServiceNumber;
        newData.nextService = trimNumber(nextServiceNumber);
        if (isFirstEdit) {
          newData.intervalNum = nextServiceNumber;
          newData.interval = trimNumber(nextServiceNumber);
        }
      }
    }

    if (oldData.interval !== newData.interval) {
      if (Number(newData.interval) > 1000) {
        const intervalNumber = newData.interval;
        newData.intervalNum = intervalNumber;
        newData.interval = trimNumber(intervalNumber);
        if (isFirstEdit) {
          newData.nextServiceNum = intervalNumber;
          newData.nextService = trimNumber(intervalNumber);
        }
      } else {
        const intervalNumber = Number(newData.interval) * 1000;
        newData.intervalNum = intervalNumber;
        newData.interval = trimNumber(intervalNumber);
        if (isFirstEdit) {
          newData.nextServiceNum = intervalNumber;
          newData.nextService = trimNumber(intervalNumber);
        }
      }
    }

    const dataUpdate = [...values];
    const realIndex = values.findIndex((v) => v === oldData);
    dataUpdate[realIndex] = newData;
    setData([...dataUpdate]);
    setEditingRow(null);
    setEditedData({});
  };

  const handleDelete = async (row) => {
    const dataDelete = [...values];
    const index = dataDelete.findIndex((v) => v === row);
    const deleteItem = dataDelete[index];

    if (remove) {
      await remove(deleteItem._id);
    }

    dataDelete.splice(index, 1);
    setData([...dataDelete]);
  };

  const handleAddNew = () => {
    setIsAdding(true);
    const emptyRow = {};
    columns.forEach((col) => {
      if (col.inputType === "date") {
        emptyRow[col.field] = new Date().toLocaleDateString("es-CL");
      } else {
        emptyRow[col.field] = "";
      }
    });
    setNewRowData({ ...emptyRow, ...defaultRow });
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewRowData({});
  };

  const handleSaveNew = () => {
    setData([...values, normalizeServiceRow(newRowData)]);
    setIsAdding(false);
    setNewRowData({});
  };

  const getRowColor = (rowData) => {
    if (kms) {
      const nkms = Number(kms);
      const intervalNum = Number(rowData.intervalNum) || 0;
      const nextNum = Number(rowData.nextServiceNum) || 0;
      const isInspection = intervalNum === 0 && nextNum === 0;

      if (isInspection) {
        return rowData.reset
          ? "bg-green-500/10 border-green-500/30"
          : "bg-red-500/10 border-red-500/30";
      }

      const effective = effectiveNextKm(rowData);

      if (effective > 0 && nkms >= effective) {
        return "bg-red-500/10 border-red-500/30";
      }
      if (effective > 0 && effective - nkms <= 1000) {
        return "bg-yellow-500/10 border-yellow-500/30";
      }
      return "bg-green-500/10 border-green-500/30";
    }
    return "bg-sidebar-accent border-sidebar-accent";
  };

  const renderCell = (row, col, isEditing, rowIndex) => {
    const value = isEditing ? editedData[col.field] : row[col.field];

    if (isEditing && col.editable !== false && col.inputType !== "readonly") {
      if (col.lookup) {
        return (
          <select
            value={value || ""}
            onChange={(e) =>
              setEditedData({ ...editedData, [col.field]: e.target.value })
            }
            className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
          >
            <option value="">Seleccionar...</option>
            {Object.entries(col.lookup).map(([key, val]) => (
              <option key={key} value={val}>
                {val}
              </option>
            ))}
          </select>
        );
      }
      if (col.inputType === "date") {
        return (
          <input
            type="date"
            value={toIsoDate(value)}
            onChange={(e) =>
              setEditedData({
                ...editedData,
                [col.field]: toEsClDate(e.target.value),
              })
            }
            className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
          />
        );
      }
      return (
        <input
          type={col.inputType === "number" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) =>
            setEditedData({ ...editedData, [col.field]: e.target.value })
          }
          className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
        />
      );
    }

    if (col.field === "nextService") {
      // Si fue reseteado, el nextService guardado; si está vacío, el intervalo.
      const effective = effectiveNextKm(row);
      return (
        <span className="text-sm text-gray-300">
          {effective > 0 ? trimNumber(effective) : "-"}
        </span>
      );
    }

    return <span className="text-sm text-gray-300">{value || "-"}</span>;
  };

  return (
    <>
      <div className="w-full space-y-4">
        <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-xl font-bold text-white">{title}</h2>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 md:flex-initial md:w-64">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  sx={{ fontSize: 20 }}
                />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-sidebar-accent text-white rounded-lg border border-sidebar-accent focus:border-blue-500 focus:outline-none placeholder-gray-500 text-sm"
                />
              </div>

              <button
                onClick={exportToCsv}
                className="p-2 bg-sidebar-accent hover:bg-sidebar-hover text-white rounded-lg transition-colors border border-sidebar-accent hover:border-green-500/50"
                title="Exportar CSV"
              >
                <SaveAltIcon sx={{ fontSize: 20 }} />
              </button>
              <button
                onClick={exportToPdf}
                className="p-2 bg-sidebar-accent hover:bg-sidebar-hover text-white rounded-lg transition-colors border border-sidebar-accent hover:border-red-500/50"
                title="Exportar PDF"
              >
                <PictureAsPdfIcon sx={{ fontSize: 20 }} />
              </button>

              {!navigate && (
                <button
                  onClick={handleAddNew}
                  disabled={isAdding}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                  Agregar
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-sidebar-accent border-b border-sidebar-accent">
                  {columns.map((col) => (
                    <th
                      key={col.field}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider"
                    >
                      {col.title}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sidebar-accent">
                {isAdding && (
                  <tr className="bg-blue-500/10 border-2 border-blue-500/30">
                    {columns.map((col) => (
                      <td key={col.field} className="px-4 py-3">
                        {col.inputType === "readonly" ? (
                          <span className="text-sm text-gray-300">
                            {newRowData[col.field] || "-"}
                          </span>
                        ) : col.lookup ? (
                          <select
                            value={newRowData[col.field] || ""}
                            onChange={(e) =>
                              setNewRowData({
                                ...newRowData,
                                [col.field]: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
                          >
                            <option value="">Seleccionar...</option>
                            {Object.entries(col.lookup).map(([key, val]) => (
                              <option key={key} value={val}>
                                {val}
                              </option>
                            ))}
                          </select>
                        ) : col.inputType === "date" ? (
                          <input
                            type="date"
                            value={toIsoDate(newRowData[col.field])}
                            onChange={(e) =>
                              setNewRowData({
                                ...newRowData,
                                [col.field]: toEsClDate(e.target.value),
                              })
                            }
                            className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
                          />
                        ) : (
                          <input
                            type={col.inputType === "number" ? "number" : "text"}
                            value={newRowData[col.field] ?? ""}
                            onChange={(e) =>
                              setNewRowData({
                                ...newRowData,
                                [col.field]: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 bg-sidebar-bg text-white rounded border border-sidebar-accent focus:border-blue-500 focus:outline-none text-sm"
                            placeholder={col.title}
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={handleSaveNew}
                          className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                          title="Guardar"
                        >
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </button>
                        <button
                          onClick={handleCancelAdd}
                          className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                          title="Cancelar"
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {paginatedData.map((row, index) => {
                  const globalIndex = values.findIndex((v) => v === row);
                  const isEditing = editingRow === index;
                  const rowColor = getRowColor(row);

                  return (
                    <tr
                      key={index}
                      className={`hover:bg-sidebar-hover transition-colors border ${rowColor}`}
                    >
                      {columns.map((col) => (
                        <td key={col.field} className="px-4 py-3">
                          {renderCell(row, col, isEditing, index)}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(row, index)}
                                className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                title="Guardar"
                              >
                                <CheckIcon sx={{ fontSize: 18 }} />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                title="Cancelar"
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </button>
                            </>
                          ) : (
                            <>
                              {!navigate && (
                                <button
                                  onClick={() => handleEdit(row, index)}
                                  className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                  title="Editar"
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(row)}
                                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                title="Eliminar"
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </button>
                              {actions && (
                                <button
                                  onClick={(e) => actions.onClick(e, row)}
                                  className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                                  title="Reset KMs"
                                >
                                  {actions.icon()}
                                </button>
                              )}
                              {onRevisado && (
                                <button
                                  onClick={() => onRevisado(row)}
                                  className={`p-1.5 rounded transition-colors ${
                                    row.reset
                                      ? "bg-green-600 hover:bg-green-700 text-white"
                                      : "bg-gray-600 hover:bg-gray-500 text-gray-300"
                                  }`}
                                  title={row.reset ? "Revisado" : "Marcar como revisado"}
                                >
                                  {row.reset ? (
                                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                                  )}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {paginatedData.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No se encontraron resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-sidebar-accent px-4 py-3 border-t border-sidebar-accent">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  Mostrando {currentPage * pageSize + 1} -{" "}
                  {Math.min((currentPage + 1) * pageSize, filteredData.length)}{" "}
                  de {filteredData.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="p-2 bg-sidebar-bg hover:bg-sidebar-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    <ChevronLeftIcon sx={{ fontSize: 20 }} />
                  </button>
                  <span className="text-sm text-white px-3">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 bg-sidebar-bg hover:bg-sidebar-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {handleSubmit && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {isDirty && (
            <span
              style={{
                background: "rgba(249, 115, 22, 0.95)",
                color: "white",
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
              }}
            >
              Cambios sin guardar
            </span>
          )}
          <Fab
            aria-label="save"
            title={isDirty ? "Guardar cambios" : "Guardar"}
            onClick={(e) => handleSubmit(e, values)}
            style={{
              backgroundColor: isDirty ? "#f97316" : "#1976d2",
              color: "white",
            }}
          >
            <SaveIcon />
          </Fab>
        </div>
      )}

      <img
        style={{ visibility: "hidden" }}
        id="logo-pdf"
        src={logo}
        height="20"
        width={20}
        alt="..."
      />
    </>
  );
};
